import { useCallback } from 'react';
import { UseFormSetError, FieldValues, Path } from 'react-hook-form';

/**
 * This hook bridges the gap between backend validation errors and react-hook-form's
 * error handling system. When the backend returns a 422 response with field-specific
 * validation errors (e.g., "username is taken"), this hook extracts those errors and
 * maps them to the corresponding form fields so they display inline with the inputs.
 *
 * Backend error format (422 response):
 * {
 *   "errorType": "validation_error",
 *   "errorCode": "validation_invalid_format",
 *   "errorMessage": "Validation failed",
 *   "validationErrors": {
 *     "username": "The username 'camille' is taken",
 *     "email": "Invalid email format"
 *   }
 * }
 */

/**
 * Error type constant for server-side validation errors. We use this to distinguish
 * server errors from client-side errors like 'required' or 'maxLength'.
 */
export const SERVER_ERROR_TYPE = 'server' as const;

/**
 * API validation error response structure from the backend
 */
export interface ApiValidationError {
  errorType: 'validation_error';
  errorCode: string;
  errorMessage: string;
  validationErrors: Record<string, string>;
}

/**
 * Type guard to check if an error is an ApiValidationError
 */
export const isApiValidationError = (
  error: unknown,
): error is ApiValidationError => {
  if (!error || typeof error !== 'object') return false;
  const err = error as Record<string, unknown>;
  return (
    err.errorType === 'validation_error' &&
    typeof err.validationErrors === 'object' &&
    err.validationErrors !== null
  );
};

/**
 * Extracts validation error response from various error shapes
 */
export const extractValidationError = (
  error: unknown,
): ApiValidationError | null => {
  if (isApiValidationError(error)) {
    return error;
  }

  // Handle errors where data is nested
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;

    // Check response.data
    if (err.response && typeof err.response === 'object') {
      const response = err.response as Record<string, unknown>;
      if (isApiValidationError(response.data)) {
        return response.data;
      }
    }

    // Check data directly
    if (isApiValidationError(err.data)) {
      return err.data;
    }
  }

  return null;
};

interface UseApiValidationErrorsOptions<T extends FieldValues> {
  setError: UseFormSetError<T>;
}

/**
 * Hook for handling API validation errors with react-hook-form
 *
 * Error types set by this hook:
 * - 'server': Used for all server-side validation errors, distinguishing them
 *   from client-side validation errors (e.g., 'required', 'maxLength')
 *
 * @example
 * ```tsx
 * const { setError, clearErrors } = useForm<FormValues>();
 * const { handleApiError } = useApiValidationErrors({ setError });
 *
 * const onSubmit = async (values: FormValues) => {
 *   // Clear any previous server errors before submitting
 *   // This prevents stale errors from persisting after user retry
 *   clearErrors();
 *
 *   try {
 *     await api.updateUser(values);
 *   } catch (error) {
 *     // Returns true if validation errors were handled, false otherwise
 *     const wasValidationError = handleApiError(error);
 *     if (!wasValidationError) {
 *       // Handle other error types (network errors, 500s, etc.)
 *       toast.error('Something went wrong');
 *     }
 *   }
 * };
 * ```
 */
export const useApiValidationErrors = <T extends FieldValues>({
  setError,
}: UseApiValidationErrorsOptions<T>) => {
  /**
   * Handles API errors by setting field-level errors for validation errors
   * @returns true if validation errors were handled, false otherwise
   */
  const handleApiError = useCallback(
    (error: unknown): boolean => {
      const validationError = extractValidationError(error);

      if (!validationError) {
        return false;
      }

      // Set each field error from the validation response
      Object.entries(validationError.validationErrors).forEach(
        ([field, message]) => {
          setError(field as Path<T>, {
            type: SERVER_ERROR_TYPE,
            message,
          });
        },
      );

      return true;
    },
    [setError],
  );

  return { handleApiError };
};
